import React, { useRef, useState } from 'react';
import { ArrowRight, Salad } from 'lucide-react';
import { Badge, Button } from './ui';
const MOBILE_BREAKPOINT = 768;
const SCROLL_SETTLE_DELAY = 700;
const CORRECTION_THRESHOLD = 20;
export function Hero() {
  const isNavigatingRef = useRef(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const scrollToPayment = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (isNavigatingRef.current) return;
    const destination = document.getElementById('bloque-pago');
    if (!destination) return;
    isNavigatingRef.current = true;
    setIsNavigating(true);
    try {
      await document.fonts.ready;
      await nextFrame();
      await nextFrame();
      scrollToDestination(destination);
      window.setTimeout(() => {
        const refreshedDestination = document.getElementById('bloque-pago');
        if (refreshedDestination) {
          const offset = getScrollOffset();
          const targetPosition = getDestinationPosition(refreshedDestination, offset);
          if (Math.abs(window.scrollY - targetPosition) > CORRECTION_THRESHOLD) {
            window.scrollTo({
              top: targetPosition,
              behavior: getScrollBehavior()
            });
          }
        }
        isNavigatingRef.current = false;
        setIsNavigating(false);
      }, SCROLL_SETTLE_DELAY);
    } catch {
      isNavigatingRef.current = false;
      setIsNavigating(false);
    }
  };
  return <section className="relative pt-4 pb-16 md:pt-6 md:pb-24 px-4 overflow-hidden pl-[16px] pr-[16px]" data-id="element-13" data-mp="358rdqj6zq89b">
      <div className="hero-background-glow absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-wine-600/25 rounded-full blur-[120px] pointer-events-none" data-id="element-14" data-mp="2qk3kijkmaamj" />

      <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-15" data-mp="2hj5fbxyfzx2d">
        <div className="inline-flex items-center gap-2 mb-4" data-id="element-16" data-mp="2hjyp77sp72oq">
          <Salad className="w-6 h-6 text-brand-400" aria-hidden="true" data-id="element-17" data-mp="2ll0gn2gdiag5" />
          <span className="text-brand-400 font-bold tracking-widest uppercase text-sm md:text-base" data-id="element-18" data-mp="s21k8hdb6o2m">
            PUNTO DIGITAL™
          </span>
        </div>

        <h1 className="font-black text-white mb-2 tracking-tight leading-[1.02] text-[30px] md:text-[58px]" data-id="element-19" data-mp="n9qf903poc37">
          ¿TUS HIJOS NO QUIEREN COMER VERDURAS?
          <br data-id="element-20" data-mp="ddne9xklijhu" />
          <span className="block mt-2 text-brand-400 text-glow font-extrabold tracking-tight leading-[1.15] text-[17px] md:text-[28px]" data-id="element-21" data-mp="de6munocvvj7">
            SIN PELEAS. SIN ENGAÑOS. SIN TROZOS VISIBLES.
          </span>
        </h1>

        <div className="relative -mt-2 mb-4 flex justify-center" data-id="element-22" data-mp="162m0p6vr47sy">
          <div className="hero-product-glow absolute inset-0 bg-brand-500/25 blur-[80px] rounded-full pointer-events-none" data-id="element-23" data-mp="111y9ak3m1oy7" />
          <img src="https://cdn.magicpatterns.com/uploads/cfJWgKisLd2ty4nHwsVXgC/mockufinal_.png" alt="Megapack Verduras Invisibles: seis tomos y cinco bonos con recetas para integrar verduras en las comidas favoritas de tus hijos" className="hero-mockup relative z-10 w-full max-w-[35rem] h-auto drop-shadow-2xl pl-[30px] pr-[30px]" width={420} height={420} loading="eager" fetchPriority="high" decoding="async" data-id="element-24" data-mp="x0l3ub4fjnrt" />
        </div>

        <p className="text-slate-200 max-w-2xl mx-auto text-[15px] md:text-[17px] leading-relaxed mt-[3px] mb-[10px]" data-id="element-25" data-mp="2b0pmp3awkuhi">
          Convertí las comidas favoritas de tus hijos en versiones más nutritivas con verduras completamente integradas, sin trozos visibles.
        </p>

        <div className="flex flex-nowrap justify-center gap-2 mb-8 overflow-x-auto" data-id="element-26" data-mp="2kx2hk4i6pedu">
          <Badge className="whitespace-nowrap" data-id="element-27" data-mp="2p10gae3lkn6d">✅ 100% Digital</Badge>
          <Badge className="whitespace-nowrap" data-id="element-28" data-mp="2tztts6ivwe6j">⚡ Acceso inmediato</Badge>
          <Badge className="whitespace-nowrap" data-id="element-29" data-mp="umheyeuskppx">🥦 6 Tomos + 5 Bonos</Badge>
        </div>

        <div className="flex flex-col items-center gap-6" data-id="element-30" data-mp="uou4bd2vq2d0">
          <Button type="button" size="md" className="w-full md:w-auto group" onClick={scrollToPayment} disabled={isNavigating} data-id="element-31" data-mp="zmc68jihbhbi">
            <span className="group-hover:scale-110 transition-transform" data-id="element-32" data-mp="3vgvwyrmxhf5q">
              🥦 SÍ, QUIERO QUE COMAN MÁS NUTRITIVO
            </span>
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-33" data-mp="9ah3f4ptqnyp" />
          </Button>

          <div className="flex flex-wrap justify-center items-center gap-6 text-sm text-slate-400" data-id="element-34" data-mp="3hpr180mol70o">
            <span className="flex items-center" data-id="element-35" data-mp="3crpixhx2xbq9">🛡️ Garantía 7 días</span>
            <span className="flex items-center" data-id="element-36" data-mp="3rg3k7oeuks4p">🔒 Pago seguro</span>
            <span className="flex items-center" data-id="element-37" data-mp="3rhenln4drxva">⚡ Acceso inmediato</span>
          </div>
        </div>
      </div>
    </section>;
}
function nextFrame() {
  return new Promise<void>(resolve => {
    requestAnimationFrame(() => resolve());
  });
}
function getScrollOffset() {
  return window.innerWidth <= MOBILE_BREAKPOINT ? 8 : 12;
}
function getDestinationPosition(destination: HTMLElement, offset: number) {
  return destination.getBoundingClientRect().top + window.scrollY - offset;
}
function scrollToDestination(destination: HTMLElement) {
  const offset = getScrollOffset();
  const position = getDestinationPosition(destination, offset);
  window.scrollTo({
    top: position,
    behavior: getScrollBehavior()
  });
}
function getScrollBehavior(): ScrollBehavior {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}